/**
 * チームの担当を設定するフォーム。
 *
 * **担当は選ばせる。入力させない。** ニックネームの綴り違いは API が弾くが、弾かれてから
 * 直すより候補から選んだ方が早い。候補（在籍者・チーム・事業）はすべて API が返したものを
 * そのまま使う——画面に enum を写すと、Backlog の選択肢が増えたときに黙ってずれる。
 *
 * 事業とチームは**新規のときだけ選べる**。既にある行で変えると、それは「別の組の担当」に
 * なってしまい、元の行が残ったまま新しい行ができる（移動ではない）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business チームの担当（リーダー / DLE / DLE サブ / DLD / TechLead）を候補から選んで設定するフォーム
 * @graph-connects useUpsertTeamAssignment [calls] 担当の設定
 */

import { useState, type ReactNode, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { MultiPicker } from '@cortex/web-design-system/components/ui/MultiPicker';
import { Picker } from '@cortex/web-design-system/components/ui/Picker';
import { useUpsertTeamAssignment } from '../../../hooks/useTeamAssignments.js';
import type { TeamAssignment, TeamAssignmentList } from '../../../lib/api/team-assignments.js';

/** 候補を Picker の形にする。 @graph-connects none */
function toOptions(values: readonly string[]): { value: string; label: string }[] {
  return values.map((value) => ({ value, label: value }));
}

/**
 * 担当の設定フォーム。`editing` を渡すと更新、渡さないと新規。
 *
 * `presetTeam` は「担当が無いチーム」から設定を始めたときに使う（そのチームで開く）。
 * @graph-connects none
 */
export function TeamAssignmentForm({
  options,
  editing,
  presetTeam,
  onDone,
}: {
  options: TeamAssignmentList;
  editing?: TeamAssignment;
  presetTeam?: string;
  onDone?: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const upsert = useUpsertTeamAssignment();

  const [business, setBusiness] = useState(editing?.business ?? '');
  const [team, setTeam] = useState(editing?.team ?? presetTeam ?? '');
  const [leader, setLeader] = useState<string[]>(
    editing?.leader === undefined || editing.leader === '' ? [] : [editing.leader],
  );
  const [dle, setDle] = useState<string[]>(editing?.dle ?? []);
  const [dleSub, setDleSub] = useState<string[]>(editing?.dleSub ?? []);
  const [dld, setDld] = useState<string[]>(editing?.dld ?? []);
  const [techLead, setTechLead] = useState<string[]>(
    editing?.techLead === undefined || editing.techLead === '' ? [] : [editing.techLead],
  );

  const members = toOptions(options.memberOptions);
  const isNew = editing === undefined;

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();
    upsert.mutate(
      {
        business,
        team,
        // Div / グループは既存の値を保つ（案件起票が Backlog の項目を埋めるのに使う）。
        ...(editing?.div === undefined || editing.div === '' ? {} : { div: editing.div }),
        ...(editing?.group === undefined || editing.group === '' ? {} : { group: editing.group }),
        leader: leader[0] ?? '',
        dle,
        dleSub,
        dld,
        techLead: techLead[0] ?? '',
      },
      { onSuccess: () => onDone?.() },
    );
  };

  return (
    <form className="space-y-3" onSubmit={submit}>
      <div className="grid grid-cols-2 gap-3">
        <Picker
          label={t('teamAssignments.form.business')}
          options={toOptions(options.businessOptions)}
          value={business === '' ? null : business}
          onChange={(value) => {
            setBusiness(value ?? '');
          }}
          disabled={!isNew}
        />
        <Picker
          label={t('teamAssignments.form.team')}
          options={toOptions(options.teamOptions)}
          value={team === '' ? null : team}
          onChange={(value) => {
            setTeam(value ?? '');
          }}
          disabled={!isNew}
          searchable
        />
      </div>

      <MultiPicker
        label={t('teamAssignments.columns.leader')}
        options={members}
        value={leader}
        onChange={setLeader}
      />
      <MultiPicker
        label={t('teamAssignments.columns.dle')}
        options={members}
        value={dle}
        onChange={setDle}
      />
      <MultiPicker
        label={t('teamAssignments.columns.dleSub')}
        options={members}
        value={dleSub}
        onChange={setDleSub}
      />
      <MultiPicker
        label={t('teamAssignments.columns.dld')}
        options={members}
        value={dld}
        onChange={setDld}
      />
      <MultiPicker
        label={t('teamAssignments.columns.techLead')}
        options={members}
        value={techLead}
        onChange={setTechLead}
      />

      <p className="text-xs text-[var(--color-text-muted)]">{t('teamAssignments.ownersHint')}</p>

      {upsert.isError && (
        <p className="text-sm text-[var(--color-error)]">{upsert.error.message}</p>
      )}

      <div className="flex gap-2">
        <Button type="submit" size="sm" loading={upsert.isPending}>
          {upsert.isPending ? t('teamAssignments.saving') : t('teamAssignments.save')}
        </Button>
        <Button type="button" size="sm" variant="secondary" onClick={onDone}>
          {t('teamAssignments.cancel')}
        </Button>
      </div>
    </form>
  );
}
